import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LimegearUserModel } from '../../shared/classes/limegearUser.model';
import { Observable, BehaviorSubject } from 'rxjs';
import { ImpersonationService } from '../../shared/services/impersonation.service';
import { Router } from '@angular/router';
import { ChangePasswordModel, ChangePasswordRequestModel } from '../models/login.model';
import { ToastService } from '../../shared/services/toast.service';
import { CurrentUserStateService } from '../../shared/services/current-user-state.service';
import { UserManagementService } from '../../shared/services/user-management.service';

@Injectable({ providedIn: 'root' })
export class LimegearAuthenticationService {
    private jwtHelper = new JwtHelperService();
    private serverUrl = 'https://' + window.location.host;
    private version = '0.5'; // TODO Get version number dynamically
    private _url = (this.serverUrl + '/api/v{version}/authentication').replace(
        '{version}',
        this.version
    );
    private authSuccess = new BehaviorSubject(false);

    constructor(
        private http: HttpClient,
        private impersonation: ImpersonationService,
        private router: Router,
        private userManagementService: UserManagementService,
        private currentUserStateService: CurrentUserStateService,
        private toastService: ToastService
    ) {}

    verifyEmailLogin(email: string): Observable<any> {
        return this.http.post<any>(`${this._url}/verify`, { username: email });
    }

    verifyOpenIdLogin(clientShortName: string): Observable<any> {
        return this.http.post<any>(`${this._url}/openid/verify`, { username: clientShortName });
    }

    login(username: string, password: string) {
        return this.http.post<any>(this._url, { username, password });
    }

    refreshToken() {
        return this.http.post<any>(`${this._url}/refresh`, null);
    }

    loginSetUserWithAzureADAccount(): Observable<any> {
        return this.http.get<any>(this._url + '/SSOLogin');
    }

    loginSetUserWithOpenId(): Observable<any> {
        return this.http.get<any>(this._url + '/openid/login');
    }

    loginSetUser(user: LimegearUserModel, firstAuth = true) {
        // login successful if there's a jwt token in the response
        if (user?.token) {
            if (firstAuth) {
                this.authSuccess.next(!!user?.token);
            }
            this.userManagementService.init();

            //// Special Logic for SimpleNexus Admins
            //if (user.isSuperUser && user.clientShortName == 'LBA') {
            //    this.impersonation.startImpersonatingClient('Big Home');
            //}
        }
        return user;
    }

    changePassword(
        changePasswordRequest: ChangePasswordModel
    ): Observable<ChangePasswordRequestModel> {
        return this.http.patch<ChangePasswordRequestModel>(this._url, changePasswordRequest);
    }

    public isLoggedIn(): Promise<boolean> {
        if (this.currentUserStateService.isLoggedIn()) {
            return Promise.resolve(true);
        } else {
            return this.refreshToken()
                .toPromise()
                .then((ref) => {
                    this.currentUserStateService.initCurrentUser(ref.token);
                    return this.currentUserStateService.isLoggedIn();
                })
                .catch((err) => {
                    return false;
                });
        }
    }

    logout() {
        this.impersonation.stopImpersonatingClient();

        this.toastService.resetAll();

        // if (!!this.msalAuth.getAccount()) {
        //     this.msalAuth.logout();
        // }
        this.router.navigate(['/login']);

        this.logoutRequest().subscribe((res) => {
            window.location.reload();
        });
    }

    logoutRequest(): Observable<any> {
        return this.http.post<any>(`${this._url}/logout`, null);
    }

    isClientPowerUser(): boolean {
        let rc = false;
        if (
            this.currentUserStateService.isLoggedIn() &&
            this.currentUserStateService.currentUser?.isClientPowerUser &&
            !(
                this.currentUserStateService.currentUser?.impersonation &&
                this.currentUserStateService.currentUser?.impersonation.loginEmail
            )
        ) {
            rc = true;
        }

        return rc;
    }

    verifySSOProvider(username: string): Observable<object> {
        return this.http.post<any>(`${this._url}/openid/verify`, { username: username });
    }

    //for events that need to trigger on login and never again
    public onAuthSuccess(): Observable<boolean> {
        return this.authSuccess.asObservable();
    }

    public getOpenIdConfigs() {
        return {
            configs: [
                {
                    name: 'test',
                    clientId: '0oaopwj8vXTSxV5gT5d6',
                    issuer: 'https://dev-03884499.okta.com/oauth2/default',
                },
                {
                    name: 'silverton',
                    clientId: '0oa1j0m68x1MWyPnb1d8',
                    issuer: 'https://silverton.okta.com',
                },
            ],
        };
    }
}
